<template>
  <nav class="app-topnav navbar navbar-expand-lg w-100 position-sticky top-0 z-index-sticky">
    <div class="container-fluid app-topnav-inner">
      <!-- Brand Logo -->
      <router-link class="app-topnav-brand" to="/home" aria-label="Progress IMS">
        <img class="app-topnav-brand-logo" :src="logo" alt="Progress IMS" />
      </router-link>

      <!-- Mobile toggler -->
      <button
        class="navbar-toggler app-topnav-toggler"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#appTopnav"
        aria-controls="appTopnav"
        aria-expanded="false"
        aria-label="Menyu"
      >
        <span class="app-topnav-toggler-lines" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>

      <!-- Navbar Collapse Rail & Actions -->
      <div id="appTopnav" class="collapse navbar-collapse">
        <!-- Navigation Rail -->
        <div class="app-topnav-rail">
          <ul class="navbar-nav app-topnav-links">
            <li
              v-for="item in navItems"
              :key="item.key"
              class="nav-item"
              :class="{ 'is-active': isActive(item) }"
            >
              <!-- Menu with Submenu Dropdown -->
              <el-dropdown
                v-if="item.list && item.list.length > 0"
                trigger="hover"
                placement="bottom-start"
                popper-class="app-topnav-el-popper"
              >
                <span
                  class="nav-link cursor-pointer"
                  :class="{ 'is-active': isActive(item), 'is-in-progress': item.inProgress }"
                  @click="item.inProgress ? showWipNotice(item.label) : null"
                >
                  <i :class="item.icon" class="app-topnav-link-icon me-1" aria-hidden="true"></i>
                  <span class="app-topnav-link-label">{{ item.label }}</span>
                  <span v-if="item.inProgress" class="app-topnav-tag-wip" title="İnkişaf mərhələsindədir (İşlər aparılır)">
                    <i class="bi bi-hourglass-split"></i>
                  </span>
                  <i class="bi bi-chevron-down nav-link-arrow ms-1" aria-hidden="true"></i>
                </span>
                <template #dropdown>
                  <el-dropdown-menu class="app-topnav-dropdown-menu">
                    <el-dropdown-item
                      v-for="sub in item.list"
                      :key="sub.itemName"
                      :disabled="sub.inProgress"
                      :class="{
                        'is-active': isSubActive(sub.href),
                        'is-in-progress': sub.inProgress
                      }"
                      @click="navigateTo(sub)"
                    >
                      <div class="d-flex align-items-center justify-content-between gap-3 w-100">
                        <div class="d-flex align-items-center gap-2">
                          <span
                            v-if="sub.icon"
                            class="app-dropdown-sub-icon-box"
                            :style="{
                              color: sub.inProgress ? 'var(--app-text-muted, #94a3b8)' : (sub.color || 'var(--bs-info)'),
                              backgroundColor: sub.inProgress ? 'rgba(148, 163, 184, 0.12)' : ((sub.color || '#0b4f9c') + '1a')
                            }"
                          >
                            <i :class="sub.icon"></i>
                          </span>
                          <span class="dropdown-item-label" :class="{ 'text-muted': sub.inProgress }">
                            {{ sub.itemName }}
                          </span>
                        </div>
                        <span v-if="sub.inProgress" class="app-dropdown-item-wip-badge">
                          <i class="bi bi-hourglass-split me-1"></i>İşlər gedir
                        </span>
                      </div>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>

              <!-- Single Link Menu -->
              <router-link
                v-else
                :to="item.inProgress ? '' : item.to"
                class="nav-link"
                :class="{ 'is-in-progress': item.inProgress }"
                @click="onSingleNavClick($event, item)"
              >
                <i :class="item.icon" class="app-topnav-link-icon me-1" aria-hidden="true"></i>
                <span class="app-topnav-link-label">{{ item.label }}</span>
                <span v-if="item.inProgress" class="app-topnav-tag-wip" title="İnkişaf mərhələsindədir (İşlər aparılır)">
                  <i class="bi bi-hourglass-split"></i>
                </span>
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Right Actions (User Menu) -->
        <div class="app-topnav-actions">
          <!-- User Menu -->
          <div class="dropdown app-topnav-user">
            <button
              class="app-topnav-user-btn"
              type="button"
              data-bs-toggle="dropdown"
              data-bs-auto-close="outside"
              aria-expanded="false"
            >
              <span class="app-topnav-user-avatar" aria-hidden="true">{{ userInitials }}</span>
              <span class="app-topnav-user-meta d-none d-lg-flex">
                <span class="app-topnav-user-name">{{ userName }}</span>
                <span v-if="companyName" class="app-topnav-user-company">{{ companyName }}</span>
              </span>
              <i class="bi bi-chevron-down app-topnav-user-caret" aria-hidden="true"></i>
            </button>

            <ul class="dropdown-menu dropdown-menu-end app-topnav-dropdown">
              <li class="app-topnav-dropdown-head">
                <div class="app-topnav-dropdown-profile">
                  <span class="app-topnav-dropdown-avatar">{{ userInitials }}</span>
                  <span class="app-topnav-dropdown-meta">
                    <strong>{{ userName }}</strong>
                    <small v-if="companyName">{{ companyName }}</small>
                  </span>
                </div>
              </li>

              <!-- Theme Toggle -->
              <li class="app-topnav-dropdown-section">
                <button
                  type="button"
                  class="dropdown-item d-flex align-items-center justify-content-between"
                  @click.stop="onToggleTheme"
                >
                  <div class="d-flex align-items-center gap-2">
                    <span class="app-topnav-dropdown-icon" aria-hidden="true">
                      <i :class="isDarkTheme ? 'bi bi-sun-fill text-warning' : 'bi bi-moon-stars-fill'"></i>
                    </span>
                    <span class="app-topnav-dropdown-copy">
                      <span class="app-topnav-dropdown-label">{{ isDarkTheme ? 'İşıqlı rejim' : 'Qaranlıq rejim' }}</span>
                      <span class="app-topnav-dropdown-hint">{{ isDarkTheme ? 'Açıq görünüşə keç' : 'Qaranlıq rejimə keç' }}</span>
                    </span>
                  </div>
                  <span class="app-topnav-theme-toggle-icon">
                    <i :class="isDarkTheme ? 'bi bi-toggle-on text-primary' : 'bi bi-toggle-off text-muted'" style="font-size: 1.35rem;"></i>
                  </span>
                </button>
              </li>

              <!-- Language Selector -->
              <li class="app-topnav-dropdown-section">
                <div class="app-topnav-dropdown-lang-wrapper">
                  <div class="app-topnav-dropdown-hint mb-1 px-1 fw-bold text-uppercase" style="font-size: 0.68rem; letter-spacing: 0.04em;">
                    Dil seçimi
                  </div>
                  <div class="app-topnav-lang-grid">
                    <button
                      v-for="lang in languages"
                      :key="lang.code"
                      type="button"
                      class="app-topnav-lang-chip"
                      :class="{ active: currentLocale === lang.code }"
                      :title="lang.name"
                      @click.stop.prevent="setLanguage(lang.code)"
                    >
                      <span class="chip-flag-box">
                        <!-- Azerbaijan -->
                        <svg v-if="lang.code === 'az'" class="flag-svg" viewBox="0 0 1200 600" xmlns="http://www.w3.org/2000/svg">
                          <rect width="1200" height="200" fill="#0092bc"/>
                          <rect y="200" width="1200" height="200" fill="#e4002b"/>
                          <rect y="400" width="1200" height="200" fill="#509e2f"/>
                          <circle cx="585" cy="300" r="90" fill="#ffffff"/>
                          <circle cx="605" cy="300" r="72" fill="#e4002b"/>
                          <g transform="translate(642, 300)">
                            <rect x="-22" y="-22" width="44" height="44" fill="#ffffff"/>
                            <rect x="-22" y="-22" width="44" height="44" fill="#ffffff" transform="rotate(45)"/>
                          </g>
                        </svg>

                        <!-- English / United Kingdom -->
                        <svg v-else-if="lang.code === 'en'" class="flag-svg" viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg">
                          <clipPath id="uk-flag-clip"><path d="M0 0v30h60V0z"/></clipPath>
                          <clipPath id="uk-flag-diag"><path d="M30 15h30v15zv15H0zH0V0zV0h30z"/></clipPath>
                          <g clip-path="url(#uk-flag-clip)">
                            <path d="M0 0v30h60V0z" fill="#012169"/>
                            <path d="M0 0l60 30m0-30L0 30" stroke="#ffffff" stroke-width="6"/>
                            <path d="M0 0l60 30m0-30L0 30" clip-path="url(#uk-flag-diag)" stroke="#C8102E" stroke-width="4"/>
                            <path d="M30 0v30M0 15h60" stroke="#ffffff" stroke-width="10"/>
                            <path d="M30 0v30M0 15h60" stroke="#C8102E" stroke-width="6"/>
                          </g>
                        </svg>

                        <!-- Turkey -->
                        <svg v-else-if="lang.code === 'tr'" class="flag-svg" viewBox="0 0 1200 800" xmlns="http://www.w3.org/2000/svg">
                          <rect width="1200" height="800" fill="#E30A17"/>
                          <circle cx="480" cy="400" r="200" fill="#ffffff"/>
                          <circle cx="530" cy="400" r="160" fill="#E30A17"/>
                          <polygon fill="#ffffff" points="0,-48 28.2,38.8 -45.7,-14.8 45.7,-14.8 -28.2,38.8" transform="translate(680, 400) rotate(-18)"/>
                        </svg>

                        <!-- Russia -->
                        <svg v-else-if="lang.code === 'ru'" class="flag-svg" viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
                          <rect width="900" height="200" fill="#ffffff"/>
                          <rect y="200" width="900" height="200" fill="#0039A6"/>
                          <rect y="400" width="900" height="200" fill="#D52B1E"/>
                        </svg>
                      </span>
                      <span class="chip-code">{{ lang.code.toUpperCase() }}</span>
                    </button>
                  </div>
                </div>
              </li>

              <!-- Account actions -->
              <li class="app-topnav-dropdown-section">
                <router-link
                  class="dropdown-item"
                  :to="{ path: '/change-password' }"
                  @click="closeMobileMenu"
                >
                  <span class="app-topnav-dropdown-icon" aria-hidden="true">
                    <i class="bi bi-key-fill"></i>
                  </span>
                  <span class="app-topnav-dropdown-copy">
                    <span class="app-topnav-dropdown-label">Şifrəni dəyiş</span>
                    <span class="app-topnav-dropdown-hint">Hesabınızın şifrəsini yeniləyin</span>
                  </span>
                </router-link>
              </li>

              <!-- Sign out -->
              <li class="app-topnav-dropdown-footer">
                <button type="button" class="dropdown-item is-danger" @click="signOut">
                  <span class="app-topnav-dropdown-icon is-danger" aria-hidden="true">
                    <i class="bi bi-box-arrow-right"></i>
                  </span>
                  <span class="app-topnav-dropdown-copy">
                    <span class="app-topnav-dropdown-label">Etibarlı çıxış</span>
                    <span class="app-topnav-dropdown-hint">Sistemdən çıxış edin</span>
                  </span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { ElMessage } from "element-plus";
import CryptoJS from "crypto-js";
import logoLight from "@/assets/img/logos/progressico2.png";
import logoDark from "@/assets/img/logos/progressico2white.png";
import { getTheme, toggleTheme } from "@/project/Source/theme";

const navItems = [
  {
    key: "home",
    label: "Ana səhifə",
    icon: "bi bi-house-door-fill",
    to: "/home",
    path: "home",
    list: null,
  },
  {
    key: "dictionary",
    label: "Soraqçalar",
    icon: "bi bi-journal-bookmark-fill",
    to: "/dictionary",
    path: "dictionary",
    list: null,
  },
  {
    key: "crm",
    label: "CRM",
    icon: "bi bi-people-fill",
    to: "/crm/person",
    path: "crm",
    list: [
      { href: "/crm/person", itemName: "Fiziki şəxslər", icon: "bi bi-person-badge-fill", color: "#38bdf8" },
      { href: "/crm/company", itemName: "Hüquqi şəxslər", icon: "bi bi-building", color: "#818cf8" },
      { href: "/documents/non", itemName: "Müştəri məmnuniyyəti", icon: "bi bi-emoji-smile-fill", color: "#fbbf24", inProgress: true },
      { href: "/crm/task", itemName: "Təqvim", icon: "bi bi-calendar-event-fill", color: "#34d399" },
      { href: "/documents/non", itemName: "Şikayətlər", icon: "bi bi-exclamation-octagon-fill", color: "#f87171", inProgress: true },
    ],
  },
  {
    key: "documents",
    label: "Yığım sığorta",
    icon: "bi bi-shield-lock-fill",
    to: "/documents/contracts",
    path: "documents",
    list: [
      { href: "/documents/contracts", itemName: "Korporativ müqavilələr", icon: "bi bi-briefcase-fill", color: "#38bdf8" },
      { href: "/documents/quotas", itemName: "Ərizələr", icon: "bi bi-file-earmark-text-fill", color: "#a78bfa" },
      { href: "/documents/policies", itemName: "Müqavilələr", icon: "bi bi-file-earmark-check-fill", color: "#34d399" },
      { href: "/documents/calculyator", itemName: "Kalkulyator", icon: "bi bi-calculator-fill", color: "#fbbf24", inProgress: true },
    ],
  },
  {
    key: "other",
    label: "Digər məhsullar",
    icon: "bi bi-grid-fill",
    to: "/documents/non",
    path: "other",
    inProgress: true,
    list: [
      { href: "/documents/non", itemName: "Təkliflər", icon: "bi bi-file-earmark-plus-fill", color: "#38bdf8", inProgress: true },
      { href: "/documents/non", itemName: "Müqavilələr", icon: "bi bi-journal-text", color: "#34d399", inProgress: true },
    ],
  },
  {
    key: "underwriting",
    label: "Anderayting",
    icon: "bi bi-patch-check-fill",
    to: "/documents/requests",
    path: "underwriting",
    list: [
      { href: "/documents/requests", itemName: "Sorğular", icon: "bi bi-inbox-fill", color: "#38bdf8" },
      { href: "/documents/non", itemName: "Məhsullar", icon: "bi bi-box-seam-fill", color: "#a78bfa", inProgress: true },
      { href: "/documents/non", itemName: "Tariflər", icon: "bi bi-percent", color: "#fbbf24", inProgress: true },
    ],
  },
  {
    key: "events",
    label: "Sığorta hadisələri",
    icon: "bi bi-heart-pulse-fill",
    to: "/documents/non",
    path: "events",
    inProgress: true,
    list: [
      { href: "/documents/non", itemName: "Müraciətlər", icon: "bi bi-megaphone-fill", color: "#38bdf8", inProgress: true },
      { href: "/documents/non", itemName: "İşlər", icon: "bi bi-card-checklist", color: "#818cf8", inProgress: true },
      { href: "/documents/non", itemName: "Təsdiqlənmə", icon: "bi bi-clipboard2-check-fill", color: "#34d399", inProgress: true },
      { href: "/documents/non", itemName: "Ödəniş", icon: "bi bi-cash-stack", color: "#f87171", inProgress: true },
    ],
  },
  {
    key: "reports",
    label: "Hesabatlar",
    icon: "bi bi-pie-chart-fill",
    to: "/documents/non",
    path: "reports",
    inProgress: true,
    list: [
      { href: "/documents/non", itemName: "Dövlət qurum hesabatları", icon: "bi bi-bank2", color: "#38bdf8", inProgress: true },
      { href: "/documents/non", itemName: "PowerBI", icon: "bi bi-bar-chart-line-fill", color: "#fbbf24", inProgress: true },
    ],
  },
];

export default {
  name: "Navbar",
  data() {
    return {
      navItems,
      theme: getTheme(),
      currentLocale: "az",
      languages: [
        { code: "az", name: "Azərbaycan", flag: "🇦🇿" },
        { code: "en", name: "English", flag: "🇬🇧" },
        { code: "tr", name: "Türkçe", flag: "🇹🇷" },
        { code: "ru", name: "Русский", flag: "🇷🇺" },
      ],
    };
  },
  computed: {
    isDarkTheme() {
      return this.theme === "dark";
    },
    logo() {
      return this.isDarkTheme ? logoDark : logoLight;
    },
    currentLanguageFlag() {
      const lang = this.languages.find((item) => item.code === this.currentLocale);
      return lang ? lang.flag : "🇦🇿";
    },
    user() {
      try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return {};
        return JSON.parse(
          CryptoJS.AES.decrypt(raw, "secret_key").toString(CryptoJS.enc.Utf8)
        );
      } catch {
        return {};
      }
    },
    userName() {
      return this.user.fullName || this.user.userName || "İstifadəçi";
    },
    companyName() {
      return this.user.companyName || "Progress Life";
    },
    userInitials() {
      const name = (this.userName || "").trim();
      if (!name || name === "İstifadəçi") {
        return "PL";
      }
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
      }
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    },
  },
  methods: {
    onToggleTheme() {
      this.theme = toggleTheme();
    },
    setLanguage(code) {
      this.currentLocale = code;
      this.closeMobileMenu();
    },
    isActive(item) {
      const routeFirst = this.$route.path.split("/")[1] || "home";
      if (item.path === routeFirst) return true;
      if (item.list) {
        return item.list.some((sub) => sub.href === this.$route.path);
      }
      return false;
    },
    isSubActive(href) {
      return this.$route.path === href;
    },
    navigateTo(sub) {
      if (!sub) return;
      const href = typeof sub === "string" ? sub : sub.href;
      const isInProgress = typeof sub === "object" ? sub.inProgress : false;
      const name = typeof sub === "object" ? sub.itemName : "";

      if (isInProgress || href === "/documents/non") {
        this.showWipNotice(name);
        return;
      }
      this.$router.push(href);
      this.closeMobileMenu();
    },
    onSingleNavClick(event, item) {
      if (item.inProgress || item.to === "/documents/non") {
        event.preventDefault();
        event.stopPropagation();
        this.showWipNotice(item.label);
        return;
      }
      this.closeMobileMenu();
    },
    showWipNotice(title) {
      ElMessage({
        message: `"${title || 'Bu'}" bölməsi üzərində işlər aparılır. Tezliklə istifadəyə veriləcək.`,
        type: "warning",
        duration: 3500,
        showClose: true,
      });
    },
    closeMobileMenu() {
      const collapse = document.getElementById("appTopnav");
      if (collapse && collapse.classList.contains("show")) {
        collapse.classList.remove("show");
      }
    },
    signOut() {
      window.localStorage.setItem(
        "jwt",
        CryptoJS.AES.encrypt(JSON.stringify("none"), "secret_key").toString()
      );
      window.localStorage.setItem(
        "roles",
        CryptoJS.AES.encrypt(JSON.stringify([{ id: null }]), "secret_key").toString()
      );
      window.localStorage.setItem(
        "user",
        CryptoJS.AES.encrypt(JSON.stringify([{ id: null }]), "secret_key").toString()
      );
      window.location.href = window.location.origin + "/signIn";
    },
  },
};
</script>

<style scoped lang="scss">
.nav-link.cursor-pointer {
  cursor: pointer;
}

.nav-link-arrow {
  font-size: 0.65rem;
  opacity: 0.65;
  margin-left: 0.25rem;
  transition: transform 0.2s ease;
}

.nav-item:hover .nav-link-arrow,
.el-dropdown:hover .nav-link-arrow {
  transform: rotate(180deg);
  opacity: 1;
}

.app-topnav-tag-wip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 19px;
  height: 19px;
  border-radius: 50%;
  font-size: 0.68rem;
  color: #d97706;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.28);
  margin-left: 5px;
  vertical-align: middle;
  pointer-events: none;
  flex-shrink: 0;
}

[data-theme="dark"] .app-topnav-tag-wip {
  color: #fbbf24;
  background: rgba(245, 158, 11, 0.16);
  border-color: rgba(245, 158, 11, 0.35);
}

.nav-link.is-in-progress {
  opacity: 0.88;
}

.app-topnav-dropdown-lang-wrapper {
  padding: 0.35rem 0.5rem;
}

.app-topnav-lang-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.35rem;
}

.app-topnav-lang-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.45rem 0.25rem;
  border-radius: 9px;
  border: 1px solid var(--app-border, #e5e7eb);
  background: var(--app-surface, #ffffff);
  color: var(--app-heading, #1e293b);
  cursor: pointer;
  transition: all 0.15s ease;

  .chip-flag-box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 17px;
    border-radius: 3px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    border: 1px solid rgba(0, 0, 0, 0.12);
    flex-shrink: 0;

    .flag-svg {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  .chip-code {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    line-height: 1;
  }

  &:hover {
    border-color: var(--bs-info, #0b4f9c);
    background: color-mix(in srgb, var(--bs-info, #0b4f9c) 8%, var(--app-surface, #ffffff));
    transform: translateY(-1px);
  }

  &.active {
    border-color: #2563eb;
    background: #2563eb;
    color: #ffffff;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);

    .chip-flag-box {
      border-color: rgba(255, 255, 255, 0.4);
    }
  }
}

.app-topnav-theme-toggle-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.app-topnav-dropdown-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 1.05rem !important;
    line-height: 1;
    display: inline-block;
  }
}

[data-theme="dark"] {
  .app-topnav-lang-chip {
    background: #252f3b;
    border-color: #334155;
    color: #f1f5f9;

    .chip-flag-box {
      border-color: rgba(255, 255, 255, 0.18);
    }

    &:hover {
      background: #2e3b4b;
      border-color: #60a5fa;
    }

    &.active {
      background: #1d4ed8;
      border-color: #60a5fa;
      color: #ffffff;
      box-shadow: 0 2px 8px rgba(29, 78, 216, 0.5);
    }
  }

  .app-topnav-dropdown-icon:not(.is-danger) {
    background: rgba(59, 130, 246, 0.18) !important;
    color: #60a5fa !important;
  }

  .app-topnav-dropdown-icon.is-danger {
    background: rgba(239, 68, 68, 0.18) !important;
    color: #f87171 !important;
  }
}
</style>

<style lang="scss">
/* Top Navigation Dropdown Popper */
.app-topnav-el-popper.el-popper {
  padding: 6px !important;
  border-radius: var(--app-radius, 12px) !important;
  border: 1px solid var(--app-border, #e2e8f0) !important;
  background: var(--app-surface, #ffffff) !important;
  box-shadow: 0 12px 32px -4px rgba(0, 0, 0, 0.16), 0 4px 12px -2px rgba(0, 0, 0, 0.08) !important;
  backdrop-filter: blur(12px) !important;
  min-width: 220px !important;
  z-index: 2050 !important;
}

.app-topnav-el-popper .el-popper__arrow {
  display: none !important;
}

.app-topnav-el-popper .el-dropdown-menu {
  padding: 0 !important;
  background: transparent !important;
  border: none !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 3px !important;
}

.app-topnav-el-popper .el-dropdown-menu__item {
  padding: 7px 10px !important;
  font-size: 0.86rem !important;
  font-weight: 550 !important;
  color: var(--app-heading, #1e293b) !important;
  border-radius: var(--app-radius-sm, 8px) !important;
  transition: all 0.16s ease !important;
  line-height: 1.3 !important;

  &:hover,
  &:focus {
    background: var(--app-surface-tint, #f0f7ff) !important;
    color: var(--bs-info, #0b4f9c) !important;

    .app-dropdown-sub-icon-box {
      transform: scale(1.08);
    }
  }

  &.is-active {
    background: color-mix(in srgb, var(--bs-info, #0b4f9c) 12%, var(--app-surface, #ffffff)) !important;
    color: var(--bs-info, #0b4f9c) !important;
    font-weight: 700 !important;

    .app-dropdown-sub-icon-box {
      background: var(--bs-info, #0b4f9c) !important;
      color: #ffffff !important;
    }
  }
}

.app-dropdown-sub-icon-box {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  flex-shrink: 0;
  transition: transform 0.16s ease, background 0.16s ease, color 0.16s ease;
}

.dropdown-item-label {
  white-space: nowrap;
}

.app-dropdown-item-wip-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: 9999px;
  font-size: 0.66rem;
  font-weight: 650;
  line-height: 1.2;
  color: #d97706;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.25);
  white-space: nowrap;
  margin-left: auto;
  flex-shrink: 0;
  pointer-events: none;
}

.app-topnav-el-popper .el-dropdown-menu__item.is-disabled,
.app-topnav-el-popper .el-dropdown-menu__item.is-in-progress {
  cursor: not-allowed !important;
  opacity: 0.65;

  &:hover,
  &:focus {
    background: rgba(148, 163, 184, 0.08) !important;
    color: inherit !important;

    .app-dropdown-sub-icon-box {
      transform: none !important;
    }
  }
}

[data-theme="dark"] .app-topnav-el-popper.el-popper {
  background: #1c232b !important;
  border-color: #2e3846 !important;
  box-shadow: 0 16px 36px -6px rgba(0, 0, 0, 0.55), 0 6px 14px -3px rgba(0, 0, 0, 0.35) !important;

  .app-dropdown-item-wip-badge {
    color: #fbbf24;
    background: rgba(245, 158, 11, 0.16);
    border-color: rgba(245, 158, 11, 0.35);
  }

  .el-dropdown-menu__item {
    color: #e2e8f0 !important;

    &:hover,
    &:focus {
      background: #252f3b !important;
      color: #93c5fd !important;
    }

    &.is-active {
      background: rgba(11, 79, 156, 0.28) !important;
      color: #93c5fd !important;
    }
  }
}
</style>
